import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { CATEGORIES, CHANGED, hasDecided, readChoice, saveChoice } from "../lib/cookieConsent";

/** A short bar rather than a wall: this site has nothing to hide behind a full-screen overlay. */
export const CookieBar = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  const sync = useCallback((event) => {
    if (event?.detail?.reopen) {
      setAnalytics(readChoice()?.analytics !== false);
      setSettings(true);
      setOpen(true);
      return;
    }
    setOpen(!hasDecided());
  }, []);

  useEffect(() => {
    window.addEventListener(CHANGED, sync);
    return () => window.removeEventListener(CHANGED, sync);
  }, [sync]);

  useEffect(() => {
    // Asked on the home page, once the visitor has arrived properly — never over a route landing page.
    if (hasDecided()) return;
    setOpen(pathname === "/");
  }, [pathname]);

  if (!open) return null;

  const decide = (allow) => {
    saveChoice(allow);
    setSettings(false);
    setOpen(false);
  };

  return (
    <div className="fixed bottom-0 left-0 z-[60] w-full p-3 sm:bottom-5 sm:left-5 sm:w-auto sm:p-0"
      data-testid="cookie-bar">
      <div className="max-w-lg border-l-2 border-ochre bg-[#0b0b0b]/95 p-5 shadow-2xl backdrop-blur-md sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <p className="mono flex items-center gap-2 text-[0.55rem] uppercase tracking-[0.16em] text-ochre">
            <Cookie size={13} /> Cookies and counting
          </p>
          <button type="button" onClick={() => decide(readChoice()?.analytics !== false)}
            aria-label="Close and keep the current setting" data-testid="cookie-close"
            className="text-zinc-500 transition-colors hover:text-white">
            <X size={14} />
          </button>
        </div>

        {settings ? (
          <div className="mt-5 space-y-4" data-testid="cookie-settings">
            {CATEGORIES.map((c) => (
              <div key={c.id} className="border border-zinc-900 p-4">
                <label className="flex items-start justify-between gap-4">
                  <span>
                    <span className="block text-sm text-white">{c.label}</span>
                    <span className="mt-1 block text-xs leading-relaxed text-zinc-400">{c.detail}</span>
                  </span>
                  <input
                    type="checkbox"
                    className="mt-1 shrink-0"
                    checked={c.locked ? true : analytics}
                    disabled={c.locked}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    data-testid={`cookie-toggle-${c.id}`}
                  />
                </label>
                {c.locked ? (
                  <p className="mono mt-3 text-[0.5rem] uppercase tracking-[0.14em] text-zinc-600">
                    Always on — without it the site cannot remember what you chose
                  </p>
                ) : null}
              </div>
            ))}
            <div className="flex flex-wrap gap-3 pt-1">
              <button type="button" onClick={() => decide(analytics)} data-testid="cookie-save-settings"
                className="mono bg-ochre px-5 py-2.5 text-[0.55rem] uppercase tracking-[0.16em] text-black">
                Save my choice
              </button>
              <button type="button" onClick={() => setSettings(false)} data-testid="cookie-settings-back"
                className="mono border border-white/20 px-5 py-2.5 text-[0.55rem] uppercase tracking-[0.16em] text-zinc-300 hover:border-ochre hover:text-ochre">
                Back
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="mt-4 text-sm leading-relaxed text-zinc-300" data-testid="cookie-bar-copy">
              This site stores the choices you make so pages remember them, and counts page views anonymously on
              our own server. No advertising cookies, no cross-site tracking, no profiling, no third-party
              trackers.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button type="button" onClick={() => decide(true)} data-testid="cookie-accept"
                className="mono bg-ochre px-5 py-2.5 text-[0.55rem] uppercase tracking-[0.16em] text-black transition-opacity hover:opacity-90">
                Accept all
              </button>
              <button type="button" onClick={() => decide(false)} data-testid="cookie-decline"
                className="mono border border-white/20 px-5 py-2.5 text-[0.55rem] uppercase tracking-[0.16em] text-zinc-300 transition-colors hover:border-ochre hover:text-ochre">
                Decline analytics
              </button>
              <button type="button" onClick={() => { setAnalytics(readChoice()?.analytics !== false); setSettings(true); }}
                data-testid="cookie-manage"
                className="mono px-2 py-2.5 text-[0.55rem] uppercase tracking-[0.16em] text-zinc-400 underline transition-colors hover:text-ochre">
                Manage settings
              </button>
            </div>
            <p className="mt-4 text-xs text-zinc-500">
              Declining changes nothing about how the site works.{" "}
              <Link to="/privacy" className="text-ochre underline" data-testid="cookie-privacy-link">
                How we use your data
              </Link>.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default CookieBar;
